import axios from 'axios';
import { API_URL } from '../utils/apiConfig';

// Create axios instance with the correct base URL
const api = axios.create({
  baseURL: API_URL
});

// Helper function to get token
const getToken = () => {
  const token = localStorage.getItem('token');
  if (token) return token;
  
  const storedUser = localStorage.getItem('user');
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      return user.token;
    } catch (error) {
      console.error('Error parsing stored user:', error);
    }
  }
  
  return null;
};

// Helper function to get headers with token
const getHeaders = () => {
  const token = getToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/**
 * Form Field Service - API calls for form field operations
 */
export const formFieldService = {
  /**
   * Create form field request
   */
  createFormFieldRequest: async (documentId, fillers) => {
    try {
      const response = await api.post(
        `/documents/${documentId}/form-field-requests`,
        { fillers },
        { headers: getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating form field request:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Get form field requests for a document
   */
  getDocumentFormFieldRequests: async (documentId) => {
    try {
      const response = await api.get(
        `/documents/${documentId}/form-field-requests`,
        { headers: getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching form field requests:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * View form field request (when user clicks the link)
   */
  viewFormFieldRequest: async (requestId) => {
    try {
      const response = await api.get(
        `/form-field-requests/${requestId}/view`,
        { headers: getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error viewing form field request:', error);
      throw error.response?.data || error;
    }
  },
  /**
   * Create or update form fields for a document
   */
  createFormFields: async (documentId, formFields) => {
    try {
      const response = await api.post(
        `/documents/${documentId}/form-fields`,
        { formFields },
        { headers: getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating form fields:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Get form fields for a document
   */
  getFormFields: async (documentId) => {
    try {
      const response = await api.get(
        `/documents/${documentId}/form-fields`,
        { headers: getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching form fields:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Submit form field data (fill out a form field)
   */
  submitFormFieldData: async (fieldId, value, ipAddress = null) => {
    try {
      const response = await api.post(
        `/form-fields/${fieldId}/submit`,
        { value, ipAddress },
        { headers: getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error submitting form field data:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Get form field data for a document (view submitted data)
   */
  getFormFieldData: async (documentId) => {
    try {
      const response = await api.get(
        `/documents/${documentId}/form-field-data`,
        { headers: getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching form field data:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Delete a form field
   */
  deleteFormField: async (fieldId) => {
    try {
      const response = await api.delete(
        `/form-fields/${fieldId}`,
        { headers: getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting form field:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Update form field metadata
   */
  updateFormField: async (fieldId, updateData) => {
    try {
      const response = await api.put(
        `/form-fields/${fieldId}`,
        updateData,
        { headers: getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating form field:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Validate form field value based on field type
   */
  validateFieldValue: (fieldType, value, validation = {}) => {
    const errors = [];

    if (!value || value.trim() === '') {
      if (validation.required) {
        errors.push('This field is required');
      }
      return errors;
    }

    // Check maximum length
    if (validation.maxLength && value.length > validation.maxLength) {
      errors.push(`Value exceeds maximum length of ${validation.maxLength} characters`);
    }

    // Field-specific validation
    switch (fieldType) {
      case 'email':
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(value)) {
          errors.push('Please enter a valid email address');
        }
        break;

      case 'phone':
        const phonePattern = /^[\+]?[1-9][\d]{0,15}$/;
        if (!phonePattern.test(value.replace(/[\s\-\(\)]/g, ''))) {
          errors.push('Please enter a valid phone number');
        }
        break;

      case 'date':
        const datePattern = /^\d{2}\/\d{2}\/\d{4}$/;
        if (!datePattern.test(value)) {
          errors.push('Please enter date in MM/DD/YYYY format');
        } else {
          // Check if it's a valid date
          const [month, day, year] = value.split('/').map(Number);
          const date = new Date(year, month - 1, day);
          if (date.getMonth() !== month - 1 || date.getDate() !== day || date.getFullYear() !== year) {
            errors.push('Please enter a valid date');
          }
        }
        break;

      case 'name':
        if (value.length < 2) {
          errors.push('Name must be at least 2 characters long');
        }
        if (!/^[a-zA-Z\s\-'\.]+$/.test(value)) {
          errors.push('Name can only contain letters, spaces, hyphens, apostrophes, and periods');
        }
        break;

      case 'title':
        if (value.length < 2) {
          errors.push('Title must be at least 2 characters long');
        }
        break;

      case 'text':
        // Basic text validation
        if (validation.minLength && value.length < validation.minLength) {
          errors.push(`Value must be at least ${validation.minLength} characters long`);
        }
        break;

      default:
        break;
    }

    // Custom pattern validation
    if (validation.pattern && !new RegExp(validation.pattern).test(value)) {
      errors.push(validation.patternMessage || 'Invalid format for this field');
    }

    return errors;
  },

  /**
   * Get field type configuration
   */
  getFieldTypeConfig: (fieldType) => {
    const fieldTypes = {
      name: { 
        label: 'Name', 
        placeholder: 'Enter full name', 
        validation: { maxLength: 100, minLength: 2 },
        inputType: 'text'
      },
      title: { 
        label: 'Title', 
        placeholder: 'Enter job title', 
        validation: { maxLength: 50, minLength: 2 },
        inputType: 'text'
      },
      email: { 
        label: 'Email', 
        placeholder: 'Enter email address', 
        validation: { maxLength: 100 },
        inputType: 'email'
      },
      phone: { 
        label: 'Phone', 
        placeholder: 'Enter phone number', 
        validation: { maxLength: 20 },
        inputType: 'tel'
      },
      date: { 
        label: 'Date', 
        placeholder: 'MM/DD/YYYY', 
        validation: { maxLength: 10 },
        inputType: 'text'
      },
      text: { 
        label: 'Text', 
        placeholder: 'Enter text', 
        validation: { maxLength: 200 },
        inputType: 'text'
      }
    };

    return fieldTypes[fieldType] || fieldTypes.text;
  }
};

export default formFieldService;